import { useState } from "react";
import { Users } from "lucide-react";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { DependentAgent } from '@/types/knowledge/knowledge-base';

interface AgentAssociationDisplayProps {
  dependentAgents: DependentAgent[];
  size?: "sm" | "md" | "lg";
  maxDisplay?: number;
  className?: string;
  mode?: "pills" | "avatars" | "labeled-avatars"; // Render compact avatars-only, bordered pills, or avatars with plain name labels
  showLabel?: boolean; // Show the "Agents" label; default true
}

export function AgentAssociationDisplay({
  dependentAgents,
  size = "sm",
  maxDisplay = 3,
  className = "",
  mode = "pills",
  showLabel = true,
}: AgentAssociationDisplayProps) {
  const [showAll, setShowAll] = useState(false);

  if (!dependentAgents || dependentAgents.length === 0) {
    return (
      <div className={`flex items-center gap-1.5 ${className}`}>
        <Users className="h-4 w-4 text-gray-400" />
        <span className="text-xs text-gray-400">Not linked to a Genie</span>
      </div>
    );
  }

  const sizeMap = {
    sm: 28,
    md: 36,
    lg: 56
  };

  const avatarSize = sizeMap[size];
  const displayAgents = showAll ? dependentAgents : dependentAgents.slice(0, maxDisplay);
  const remainingCount = dependentAgents.length - maxDisplay;

  const AgentPill = ({ depAgent }: { depAgent: DependentAgent }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5 px-2 py-1 bg-white border border-gray-200 rounded-full hover:border-genie-primary/40 transition-colors">
            <GenieAvatar
              size={avatarSize}
              color={depAgent.branding?.primaryColor || "#4E9CFF"}
              secondaryColor={depAgent.branding?.secondaryColor}
              customAvatarUrl={depAgent.branding?.logoUrl || depAgent.branding?.customAvatarUrl}
              gradientEnabled={depAgent.branding?.gradientEnabled}
              gradientAngle={depAgent.branding?.gradientAngle}
              avatarBackgroundOpacity={depAgent.branding?.avatarBackgroundOpacity}
            />
            <span className="text-xs font-medium text-gray-700 max-w-[100px] truncate">
              {depAgent.name || "Unknown Agent"}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{depAgent.name || "Unknown Agent"}</p>
          {depAgent.access_level && (
            <p className="text-xs text-gray-400">Access: {depAgent.access_level}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const AgentAvatarLabeled = ({ depAgent }: { depAgent: DependentAgent }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-1.5">
            <GenieAvatar
              size={avatarSize}
              color={depAgent.branding?.primaryColor || "#4E9CFF"}
              secondaryColor={depAgent.branding?.secondaryColor}
              customAvatarUrl={depAgent.branding?.logoUrl || depAgent.branding?.customAvatarUrl}
              gradientEnabled={depAgent.branding?.gradientEnabled}
              gradientAngle={depAgent.branding?.gradientAngle}
              avatarBackgroundOpacity={depAgent.branding?.avatarBackgroundOpacity}
            />
            <span className="text-xs font-medium text-gray-700 max-w-[100px] truncate">
              {depAgent.name || "Unknown Agent"}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{depAgent.name || "Unknown Agent"}</p>
          {depAgent.access_level && (
            <p className="text-xs text-gray-400">Access: {depAgent.access_level}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const AgentAvatarOnly = ({ depAgent }: { depAgent: DependentAgent }) => (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex">
            <GenieAvatar
              size={avatarSize}
              color={depAgent.branding?.primaryColor || "#4E9CFF"}
              secondaryColor={depAgent.branding?.secondaryColor}
              customAvatarUrl={depAgent.branding?.logoUrl || depAgent.branding?.customAvatarUrl}
              gradientEnabled={depAgent.branding?.gradientEnabled}
              gradientAngle={depAgent.branding?.gradientAngle}
              avatarBackgroundOpacity={depAgent.branding?.avatarBackgroundOpacity}
            />
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p>{depAgent.name || "Unknown Agent"}</p>
          {depAgent.access_level && (
            <p className="text-xs text-gray-400">Access: {depAgent.access_level}</p>
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const renderPills = (
    <div className={`flex items-center gap-2 ${className}`}>
      {showLabel && (
        <div className="flex items-center gap-1.5">
          <Users className="h-4 w-4 text-gray-400" />
          <span className="text-xs font-medium text-gray-600">Genies:</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 flex-wrap">
        {displayAgents.map((agent) => (
          <AgentPill key={agent.id} depAgent={agent} />
        ))}
        {remainingCount > 0 && !showAll && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-2 py-1 text-xs rounded-full border-gray-200 hover:border-blue-300"
              >
                +{remainingCount} more
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-3">
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">All Associated Genies ({dependentAgents.length})</div>
                <div className="flex flex-wrap gap-1.5">
                  {dependentAgents.map((agent) => (
                    <AgentPill key={agent.id} depAgent={agent} />
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        )}
      </div>
    </div>
  );

  const renderAvatars = (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {displayAgents.map((agent) => (
        <AgentAvatarOnly key={agent.id} depAgent={agent} />
      ))}
      {remainingCount > 0 && !showAll && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-auto px-2.5 py-1 text-xs rounded-full border-gray-200 hover:border-blue-300"
            >
              +{remainingCount}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-3">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700 mb-2">All Associated Genies ({dependentAgents.length})</div>
              <div className="flex flex-wrap gap-2">
                {dependentAgents.map((agent) => (
                  <AgentPill key={agent.id} depAgent={agent} />
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  const renderLabeledAvatars = (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      {displayAgents.map((agent) => (
        <AgentAvatarLabeled key={agent.id} depAgent={agent} />
      ))}
      {remainingCount > 0 && !showAll && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="h-auto px-2.5 py-1 text-xs rounded-full border-gray-200 hover:border-blue-300"
            >
              +{remainingCount}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-96 p-3">
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700 mb-2">All Associated Genies ({dependentAgents.length})</div>
              <div className="flex flex-wrap gap-3">
                {dependentAgents.map((agent) => (
                  <AgentAvatarLabeled key={agent.id} depAgent={agent} />
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );

  if (mode === "avatars") return renderAvatars;
  if (mode === "labeled-avatars") return renderLabeledAvatars;
  return renderPills;
}